/**
 * SegmentSelector Component
 *
 * Dropdown to select a saved user segment.
 * Shows segment name and user count badge when selected.
 */

import { ChevronDown, Users, ExternalLink, Loader2 } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import type { SegmentSummary } from "../types";

interface SegmentSelectorProps {
  segments: SegmentSummary[];
  selectedSegment: SegmentSummary | null;
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onSelect: (segmentId: string) => void;
}

export function SegmentSelector({
  segments,
  selectedSegment,
  loading,
  error,
  onRetry,
  onSelect,
}: SegmentSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Error state — distinto del empty: la carga falló, no es que no haya
  // segmentos. No mandamos al usuario a "crear" lo que en realidad sí tiene.
  if (!loading && error) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users size={18} className="text-violet-500" />
          <h3 className="font-semibold text-slate-900">
            Seleccionar Audiencia
          </h3>
        </div>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users size={28} className="text-red-400" />
          </div>
          <p className="text-slate-600 mb-3">{error}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 text-violet-600 text-sm font-medium hover:text-violet-700 transition-colors"
            >
              Reintentar
            </button>
          )}
        </div>
      </div>
    );
  }

  // Empty state
  if (!loading && segments.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users size={18} className="text-violet-500" />
          <h3 className="font-semibold text-slate-900">
            Seleccionar Audiencia
          </h3>
        </div>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users size={28} className="text-slate-400" />
          </div>
          <p className="text-slate-500 mb-2">
            Todavía no tenés segmentos creados.
          </p>
          <a
            href="/segments"
            className="inline-flex items-center gap-1.5 text-violet-600 text-sm font-medium hover:text-violet-700 transition-colors"
          >
            Ir a Segmentación
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <Users size={18} className="text-violet-500" />
        <h3 className="font-semibold text-slate-900">Seleccionar Audiencia</h3>
        <span className="text-xs text-slate-400 ml-auto">Paso 1</span>
      </div>

      {/* Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => !loading && setIsOpen(!isOpen)}
          disabled={loading}
          className={`
                        w-full flex items-center justify-between px-4 py-3.5 rounded-xl border transition-all duration-200
                        ${
                          isOpen
                            ? "border-violet-300 ring-2 ring-violet-100 bg-violet-50/30"
                            : "border-slate-200 hover:border-slate-300 bg-white"
                        }
                        ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
                    `}
        >
          <span
            className={
              selectedSegment ? "text-slate-900 font-medium" : "text-slate-400"
            }
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Cargando segmentos...
              </span>
            ) : selectedSegment ? (
              selectedSegment.name
            ) : (
              "Seleccionar segmento guardado"
            )}
          </span>
          <ChevronDown
            size={18}
            className={`text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-lg z-30 overflow-hidden animate-fade-in">
            <div className="max-h-64 overflow-y-auto">
              {segments.map((seg) => (
                <button
                  key={seg.id}
                  onClick={() => {
                    onSelect(seg.id);
                    setIsOpen(false);
                  }}
                  className={`
                                        w-full flex items-center justify-between px-4 py-3 text-left transition-colors duration-150
                                        ${
                                          seg.id === selectedSegment?.id
                                            ? "bg-violet-50 text-violet-700"
                                            : "hover:bg-slate-50 text-slate-700"
                                        }
                                    `}
                >
                  <span className="font-medium text-sm">{seg.name}</span>
                  <span
                    className={`
                                        text-xs font-semibold px-2 py-0.5 rounded-full
                                        ${
                                          seg.id === selectedSegment?.id
                                            ? "bg-violet-100 text-violet-600"
                                            : "bg-slate-100 text-slate-500"
                                        }
                                    `}
                  >
                    {seg.lastKnownCount.toLocaleString()} usuarios
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Selected segment info */}
      {selectedSegment && (
        <div className="mt-4 flex items-center gap-3 p-3 bg-violet-50/50 rounded-xl border border-violet-100 animate-fade-in">
          <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Users size={16} className="text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">
              {selectedSegment.name}
            </p>
            <p className="text-xs text-slate-500">Segmento guardado</p>
          </div>
          <span className="px-3 py-1 bg-violet-600 text-white text-xs font-bold rounded-full flex-shrink-0">
            {selectedSegment.lastKnownCount.toLocaleString()} usuarios
          </span>
        </div>
      )}

      {/* Link to manage segments */}
      <div className="mt-3 text-right">
        <a
          href="/segments"
          className="text-xs text-slate-400 hover:text-violet-500 transition-colors inline-flex items-center gap-1"
        >
          Gestionar segmentos
          <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

export default SegmentSelector;
